import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Register.css';
import { SUPABASE_READY, supabase } from '../lib/supabaseClient';

function Community() {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!SUPABASE_READY) {
      setLoading(false);
      return;
    }

    async function loadUsers() {
      try {
        // Fetch all profiles
        const { data: profiles, error } = await supabase
          .from('profiles')
          .select('id, username, display_name, profile_photo_path');

        if (error) throw error;

        if (!profiles) {
          setUsers([]);
          setFilteredUsers([]);
          setLoading(false);
          return;
        }

        // For each profile, get their catch count
        const usersWithCounts = await Promise.all(
          profiles.map(async (profile) => {
            const { count, error: countError } = await supabase
              .from('catches')
              .select('*', { count: 'exact', head: true })
              .eq('user_id', profile.id);

            return {
              ...profile,
              catchCount: countError ? 0 : count || 0,
            };
          })
        );

        setUsers(usersWithCounts);
        setFilteredUsers(usersWithCounts);
      } catch (err) {
        console.error('Error loading community:', err);
        setUsers([]);
        setFilteredUsers([]);
      } finally {
        setLoading(false);
      }
    }

    loadUsers();
  }, []);

  // Handle search/filter
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(
      (user) =>
        user.username?.toLowerCase().includes(query) ||
        user.display_name?.toLowerCase().includes(query)
    );
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  if (!SUPABASE_READY) {
    return (
      <div className="page-container">
        <div className="card error-card">
          <p>Supabase is not configured.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-container">
        <div className="card"><p>Loading community members...</p></div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1 className="page-title">Community</h1>
      <p className="page-subtitle">Browse other anglers and their catches.</p>

      <div className="card">
        <input
          type="text"
          placeholder="Search by name or username..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      {filteredUsers.length === 0 ? (
        <div className="card">
          <p>No users found.</p>
        </div>
      ) : (
        <div className="community-grid">
          {filteredUsers.map((user) => (
            <Link
              key={user.id}
              to={`/user/${user.username}`}
              className="community-card"
            >
              <UserCardContent user={user} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function UserCardContent({ user }) {
  const [photoUrl, setPhotoUrl] = useState('');

  useEffect(() => {
    if (!user.profile_photo_path || !supabase) return;

    async function loadPhoto() {
      const { data, error } = await supabase.storage
        .from('profile-photos')
        .createSignedUrl(user.profile_photo_path, 3600);

      if (!error && data) {
        setPhotoUrl(data.signedUrl);
      }
    }

    loadPhoto();
  }, [user]);

  return (
    <div className="community-card-inner">
      <div className="community-card-photo">
        {photoUrl ? (
          <img src={photoUrl} alt={user.display_name} />
        ) : (
          <div className="profile-avatar-fallback">👤</div>
        )}
      </div>
      <div className="community-card-info">
        <h3>{user.display_name}</h3>
        <p className="community-card-username">@{user.username}</p>
        <p className="community-card-catches">{user.catchCount} catch{user.catchCount !== 1 ? 'es' : ''}</p>
      </div>
    </div>
  );
}

export default Community;
